import { useState } from 'react';

// ─── Types ────────────────────────────────────────────────────────────────────
type SourceType = 'journal' | 'book' | 'website' | 'conference' | 'thesis' | 'newspaper';
type CitationStyle = 'APA' | 'MLA' | 'Chicago' | 'IEEE';

interface Author {
  id: string;
  firstName: string;
  lastName: string;
}

interface ReferenceForm {
  sourceType: SourceType;
  authors: Author[];
  title: string;
  year: string;
  journal?: string;
  publisher?: string;
  url?: string;
  volume?: string;
  issue?: string;
  pages?: string;
  doi?: string;
  city?: string;
  conference?: string;
  institution?: string;
  accessDate?: string;
}

interface BuiltCitation {
  id: string;
  form: ReferenceForm;
  citations: Record<CitationStyle, { full: string; inText: string }>;
  createdAt: Date;
}

// ─── Source Type Config ───────────────────────────────────────────────────────
const SOURCE_CONFIG: Record<SourceType, { icon: string; label: string; color: string; bg: string; fields: string[] }> = {
  journal: {
    icon: 'ri-article-line',
    label: 'Journal Article',
    color: 'text-indigo-600',
    bg: 'bg-indigo-50',
    fields: ['authors', 'title', 'year', 'journal', 'volume', 'issue', 'pages', 'doi'],
  },
  book: {
    icon: 'ri-book-2-line',
    label: 'Book',
    color: 'text-amber-600',
    bg: 'bg-amber-50',
    fields: ['authors', 'title', 'year', 'publisher', 'city'],
  },
  website: {
    icon: 'ri-global-line',
    label: 'Website',
    color: 'text-cyan-600',
    bg: 'bg-cyan-50',
    fields: ['authors', 'title', 'year', 'url', 'accessDate'],
  },
  conference: {
    icon: 'ri-presentation-line',
    label: 'Conference Paper',
    color: 'text-emerald-600',
    bg: 'bg-emerald-50',
    fields: ['authors', 'title', 'year', 'conference', 'pages'],
  },
  thesis: {
    icon: 'ri-graduation-cap-line',
    label: 'Thesis/Dissertation',
    color: 'text-purple-600',
    bg: 'bg-purple-50',
    fields: ['authors', 'title', 'year', 'institution'],
  },
  newspaper: {
    icon: 'ri-newspaper-line',
    label: 'Newspaper Article',
    color: 'text-orange-600',
    bg: 'bg-orange-50',
    fields: ['authors', 'title', 'year', 'journal', 'pages'],
  },
};

const STYLE_CONFIG: Record<CitationStyle, { color: string; bg: string; border: string }> = {
  APA: { color: 'text-indigo-700', bg: 'bg-indigo-50', border: 'border-indigo-300' },
  MLA: { color: 'text-emerald-700', bg: 'bg-emerald-50', border: 'border-emerald-300' },
  Chicago: { color: 'text-amber-700', bg: 'bg-amber-50', border: 'border-amber-300' },
  IEEE: { color: 'text-cyan-700', bg: 'bg-cyan-50', border: 'border-cyan-300' },
};

// ─── Citation Formatters ──────────────────────────────────────────────────────
function formatAPA(form: ReferenceForm): { full: string; inText: string } {
  const authorStr = form.authors.length === 0
    ? 'Unknown Author'
    : form.authors.length === 1
    ? `${form.authors[0].lastName}, ${form.authors[0].firstName.charAt(0)}.`
    : form.authors.length === 2
    ? `${form.authors[0].lastName}, ${form.authors[0].firstName.charAt(0)}., & ${form.authors[1].lastName}, ${form.authors[1].firstName.charAt(0)}.`
    : `${form.authors[0].lastName}, ${form.authors[0].firstName.charAt(0)}., ${form.authors[1].lastName}, ${form.authors[1].firstName.charAt(0)}., & ${form.authors[form.authors.length - 1].lastName}, ${form.authors[form.authors.length - 1].firstName.charAt(0)}.`;

  let full = '';
  if (form.sourceType === 'journal') {
    full = `${authorStr} (${form.year}). ${form.title}. ${form.journal ?? 'Journal'}${form.volume ? `, ${form.volume}` : ''}${form.issue ? `(${form.issue})` : ''}${form.pages ? `, ${form.pages}` : ''}.${form.doi ? ` https://doi.org/${form.doi}` : ''}`;
  } else if (form.sourceType === 'website') {
    full = `${authorStr} (${form.year}). ${form.title}. Retrieved${form.accessDate ? ` ${form.accessDate}` : ''} from ${form.url ?? 'URL'}`;
  } else if (form.sourceType === 'conference') {
    full = `${authorStr} (${form.year}). ${form.title}. In *Proceedings of ${form.conference ?? 'the Conference'}*${form.pages ? ` (pp. ${form.pages})` : ''}.`;
  } else if (form.sourceType === 'thesis') {
    full = `${authorStr} (${form.year}). *${form.title}* [Doctoral dissertation]. ${form.institution ?? 'University'}.`;
  } else if (form.sourceType === 'newspaper') {
    full = `${authorStr} (${form.year}). ${form.title}. *${form.journal ?? 'Newspaper'}*${form.pages ? `, p. ${form.pages}` : ''}.`;
  } else {
    full = `${authorStr} (${form.year}). *${form.title}*. ${form.city ? `${form.city}: ` : ''}${form.publisher ?? 'Publisher'}.`;
  }

  const lastName = form.authors[0]?.lastName ?? 'Author';
  const inText = form.authors.length > 2
    ? `(${lastName} et al., ${form.year})`
    : form.authors.length === 2
    ? `(${form.authors[0].lastName} & ${form.authors[1].lastName}, ${form.year})`
    : `(${lastName}, ${form.year})`;

  return { full, inText };
}

function formatMLA(form: ReferenceForm): { full: string; inText: string } {
  const authorStr = form.authors.length === 0
    ? 'Unknown Author'
    : form.authors.length === 1
    ? `${form.authors[0].lastName}, ${form.authors[0].firstName}`
    : `${form.authors[0].lastName}, ${form.authors[0].firstName}, et al.`;

  let full = '';
  if (form.sourceType === 'journal') {
    full = `${authorStr}. "${form.title}." *${form.journal ?? 'Journal'}*${form.volume ? `, vol. ${form.volume}` : ''}${form.issue ? `, no. ${form.issue}` : ''}, ${form.year}${form.pages ? `, pp. ${form.pages}` : ''}.${form.doi ? ` DOI: ${form.doi}.` : ''}`;
  } else if (form.sourceType === 'website') {
    full = `${authorStr}. "${form.title}." *Website*, ${form.year}, ${form.url ?? 'URL'}.${form.accessDate ? ` Accessed ${form.accessDate}.` : ''}`;
  } else if (form.sourceType === 'conference') {
    full = `${authorStr}. "${form.title}." *${form.conference ?? 'Conference Proceedings'}*, ${form.year}${form.pages ? `, pp. ${form.pages}` : ''}.`;
  } else if (form.sourceType === 'thesis') {
    full = `${authorStr}. *${form.title}*. ${form.year}. ${form.institution ?? 'University'}, PhD dissertation.`;
  } else if (form.sourceType === 'newspaper') {
    full = `${authorStr}. "${form.title}." *${form.journal ?? 'Newspaper'}*, ${form.year}${form.pages ? `, p. ${form.pages}` : ''}.`;
  } else {
    full = `${authorStr}. *${form.title}*. ${form.city ? `${form.city}: ` : ''}${form.publisher ?? 'Publisher'}, ${form.year}.`;
  }

  const lastName = form.authors[0]?.lastName ?? 'Author';
  const inText = form.authors.length > 1 ? `(${lastName} et al.)` : `(${lastName})`;

  return { full, inText };
}

function formatChicago(form: ReferenceForm): { full: string; inText: string } {
  const authorStr = form.authors.length === 0
    ? 'Unknown Author'
    : form.authors.length === 1
    ? `${form.authors[0].lastName}, ${form.authors[0].firstName}`
    : form.authors.length === 2
    ? `${form.authors[0].lastName}, ${form.authors[0].firstName} and ${form.authors[1].firstName} ${form.authors[1].lastName}`
    : `${form.authors[0].lastName}, ${form.authors[0].firstName} et al.`;

  let full = '';
  if (form.sourceType === 'journal') {
    full = `${authorStr}. "${form.title}." *${form.journal ?? 'Journal'}* ${form.volume ?? ''}${form.issue ? `, no. ${form.issue}` : ''} (${form.year})${form.pages ? `: ${form.pages}` : ''}.${form.doi ? ` https://doi.org/${form.doi}.` : ''}`;
  } else if (form.sourceType === 'website') {
    full = `${authorStr}. "${form.title}." Accessed ${form.accessDate ?? form.year}. ${form.url ?? 'URL'}.`;
  } else if (form.sourceType === 'conference') {
    full = `${authorStr}. "${form.title}." Paper presented at ${form.conference ?? 'the Conference'}, ${form.year}${form.pages ? `, ${form.pages}` : ''}.`;
  } else if (form.sourceType === 'thesis') {
    full = `${authorStr}. "${form.title}." PhD diss., ${form.institution ?? 'University'}, ${form.year}.`;
  } else if (form.sourceType === 'newspaper') {
    full = `${authorStr}. "${form.title}." *${form.journal ?? 'Newspaper'}*, ${form.year}${form.pages ? `, ${form.pages}` : ''}.`;
  } else {
    full = `${authorStr}. *${form.title}*. ${form.city ? `${form.city}: ` : ''}${form.publisher ?? 'Publisher'}, ${form.year}.`;
  }

  const lastName = form.authors[0]?.lastName ?? 'Author';
  const inText = `(${lastName} ${form.year})`;

  return { full, inText };
}

function formatIEEE(form: ReferenceForm, index: number): { full: string; inText: string } {
  const authorStr = form.authors.length === 0
    ? 'Unknown Author'
    : form.authors.map(a => `${a.firstName.charAt(0)}. ${a.lastName}`).join(', ');

  let full = '';
  if (form.sourceType === 'journal') {
    full = `${authorStr}, "${form.title}," *${form.journal ?? 'Journal'}*${form.volume ? `, vol. ${form.volume}` : ''}${form.issue ? `, no. ${form.issue}` : ''}${form.pages ? `, pp. ${form.pages}` : ''}, ${form.year}.${form.doi ? ` doi: ${form.doi}.` : ''}`;
  } else if (form.sourceType === 'website') {
    full = `${authorStr}. "${form.title}." [Online]. Available: ${form.url ?? 'URL'}. [Accessed: ${form.accessDate ?? form.year}].`;
  } else if (form.sourceType === 'conference') {
    full = `${authorStr}, "${form.title}," in *Proc. ${form.conference ?? 'Conference'}*${form.pages ? `, pp. ${form.pages}` : ''}, ${form.year}.`;
  } else if (form.sourceType === 'thesis') {
    full = `${authorStr}, "${form.title}," Ph.D. dissertation, ${form.institution ?? 'Univ.'}, ${form.year}.`;
  } else if (form.sourceType === 'newspaper') {
    full = `${authorStr}, "${form.title}," *${form.journal ?? 'Newspaper'}*${form.pages ? `, p. ${form.pages}` : ''}, ${form.year}.`;
  } else {
    full = `${authorStr}, *${form.title}*. ${form.city ? `${form.city}: ` : ''}${form.publisher ?? 'Publisher'}, ${form.year}.`;
  }

  return { full, inText: `[${index + 1}]` };
}

function buildCitations(form: ReferenceForm, index: number): Record<CitationStyle, { full: string; inText: string }> {
  return {
    APA: formatAPA(form),
    MLA: formatMLA(form),
    Chicago: formatChicago(form),
    IEEE: formatIEEE(form, index),
  };
}

// ─── Main Component ───────────────────────────────────────────────────────────
export default function ReferenceBuilderPanel() {
  const [form, setForm] = useState<ReferenceForm>({
    sourceType: 'journal',
    authors: [{ id: '1', firstName: '', lastName: '' }],
    title: '',
    year: '',
  });

  const [builtCitations, setBuiltCitations] = useState<BuiltCitation[]>([]);
  const [isBuilding, setIsBuilding] = useState(false);
  const [showResult, setShowResult] = useState(false);
  const [currentCitation, setCurrentCitation] = useState<BuiltCitation | null>(null);
  const [copiedStyle, setCopiedStyle] = useState<CitationStyle | null>(null);
  const [expandedId, setExpandedId] = useState<string | null>(null);

  const srcCfg = SOURCE_CONFIG[form.sourceType];

  const updateForm = (patch: Partial<ReferenceForm>) => setForm({ ...form, ...patch });

  const addAuthor = () => {
    setForm({
      ...form,
      authors: [...form.authors, { id: Date.now().toString(), firstName: '', lastName: '' }],
    });
  };

  const removeAuthor = (id: string) => {
    if (form.authors.length > 1) {
      setForm({ ...form, authors: form.authors.filter(a => a.id !== id) });
    }
  };

  const updateAuthor = (id: string, field: 'firstName' | 'lastName', value: string) => {
    setForm({
      ...form,
      authors: form.authors.map(a => (a.id === id ? { ...a, [field]: value } : a)),
    });
  };

  const handleBuild = () => {
    setIsBuilding(true);
    setShowResult(false);
    setTimeout(() => {
      const citations = buildCitations(form, builtCitations.length);
      const newCitation: BuiltCitation = {
        id: Date.now().toString(),
        form: { ...form },
        citations,
        createdAt: new Date(),
      };
      setBuiltCitations([newCitation, ...builtCitations]);
      setCurrentCitation(newCitation);
      setIsBuilding(false);
      setShowResult(true);
      setExpandedId(newCitation.id);
    }, 800);
  };

  const handleCopy = (text: string, style: CitationStyle) => {
    navigator.clipboard.writeText(text).then(() => {
      setCopiedStyle(style);
      setTimeout(() => setCopiedStyle(null), 2000);
    });
  };

  const handleReset = () => {
    setForm({
      sourceType: 'journal',
      authors: [{ id: Date.now().toString(), firstName: '', lastName: '' }],
      title: '',
      year: '',
    });
    setShowResult(false);
    setCurrentCitation(null);
  };

  const isFormValid = form.title.trim().length > 0 && form.year.trim().length > 0 && form.authors.some(a => a.lastName.trim().length > 0);

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center gap-3 p-4 rounded-2xl bg-gradient-to-r from-purple-50 to-indigo-50 border border-purple-100">
        <div className="w-10 h-10 flex items-center justify-center rounded-xl bg-gradient-to-br from-purple-500 to-indigo-600 flex-shrink-0">
          <i className="ri-edit-box-line text-white text-lg"></i>
        </div>
        <div className="flex-1 min-w-0">
          <p className="text-sm font-extrabold text-gray-900">Reference Builder</p>
          <p className="text-xs text-gray-500">Manually build citations from scratch — fill in the fields below</p>
        </div>
        {builtCitations.length > 0 && (
          <span className="text-xs font-bold px-3 py-1.5 rounded-full bg-purple-100 text-purple-700 flex-shrink-0">
            {builtCitations.length} saved
          </span>
        )}
      </div>

      {!showResult ? (
        <>
          {/* Source Type Selector */}
          <div>
            <label className="block text-sm font-bold text-gray-700 mb-3 flex items-center gap-2">
              <i className="ri-file-list-3-line text-purple-500"></i>
              Source Type
            </label>
            <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
              {(Object.keys(SOURCE_CONFIG) as SourceType[]).map(type => {
                const cfg = SOURCE_CONFIG[type];
                return (
                  <button
                    key={type}
                    type="button"
                    onClick={() => updateForm({ sourceType: type })}
                    className={`flex items-center gap-3 p-3 rounded-2xl border-2 text-left transition-all cursor-pointer ${
                      form.sourceType === type
                        ? `${cfg.bg} border-${cfg.color.replace('text-', '')} shadow-sm`
                        : 'border-gray-200 bg-white hover:border-gray-300'
                    }`}
                  >
                    <div className={`w-8 h-8 flex items-center justify-center rounded-lg flex-shrink-0 ${form.sourceType === type ? cfg.bg : 'bg-gray-100'}`}>
                      <i className={`${cfg.icon} text-sm ${form.sourceType === type ? cfg.color : 'text-gray-400'}`}></i>
                    </div>
                    <span className={`text-sm font-bold ${form.sourceType === type ? cfg.color : 'text-gray-600'}`}>{cfg.label}</span>
                  </button>
                );
              })}
            </div>
          </div>

          {/* Authors */}
          <div>
            <label className="block text-sm font-bold text-gray-700 mb-2 flex items-center gap-2">
              <i className="ri-user-line text-purple-500"></i>
              Author(s)
            </label>
            <div className="space-y-2">
              {form.authors.map((author, idx) => (
                <div key={author.id} className="flex items-center gap-2">
                  <span className="text-xs font-bold text-gray-400 w-6 text-right flex-shrink-0">{idx + 1}.</span>
                  <input
                    type="text"
                    value={author.firstName}
                    onChange={(e) => updateAuthor(author.id, 'firstName', e.target.value)}
                    placeholder="First name"
                    className="flex-1 px-3 py-2 rounded-lg border border-gray-200 bg-white text-sm text-gray-800 placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-purple-300 focus:border-purple-400 transition-all"
                  />
                  <input
                    type="text"
                    value={author.lastName}
                    onChange={(e) => updateAuthor(author.id, 'lastName', e.target.value)}
                    placeholder="Last name"
                    className="flex-1 px-3 py-2 rounded-lg border border-gray-200 bg-white text-sm text-gray-800 placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-purple-300 focus:border-purple-400 transition-all"
                  />
                  {form.authors.length > 1 && (
                    <button
                      onClick={() => removeAuthor(author.id)}
                      className="w-8 h-8 flex items-center justify-center rounded-lg hover:bg-red-50 text-gray-400 hover:text-red-500 transition-all cursor-pointer flex-shrink-0"
                    >
                      <i className="ri-close-line text-sm"></i>
                    </button>
                  )}
                </div>
              ))}
            </div>
            <button
              onClick={addAuthor}
              className="flex items-center gap-1.5 px-3 py-1.5 mt-2 rounded-lg border border-dashed border-gray-300 text-gray-500 text-xs font-semibold hover:border-purple-400 hover:text-purple-600 hover:bg-purple-50 transition-all cursor-pointer whitespace-nowrap"
            >
              <i className="ri-add-line text-sm"></i>
              Add Another Author
            </button>
          </div>

          {/* Title & Year */}
          <div className="grid md:grid-cols-3 gap-4">
            <div className="md:col-span-2">
              <label className="block text-sm font-bold text-gray-700 mb-2 flex items-center gap-2">
                <i className="ri-text text-purple-500"></i>
                Title
              </label>
              <input
                type="text"
                value={form.title}
                onChange={(e) => updateForm({ title: e.target.value })}
                placeholder="Enter the full title of the work"
                className="w-full px-3 py-2 rounded-lg border border-gray-200 bg-white text-sm text-gray-800 placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-purple-300 focus:border-purple-400 transition-all"
              />
            </div>
            <div>
              <label className="block text-sm font-bold text-gray-700 mb-2 flex items-center gap-2">
                <i className="ri-calendar-line text-purple-500"></i>
                Year
              </label>
              <input
                type="text"
                value={form.year}
                onChange={(e) => updateForm({ year: e.target.value })}
                placeholder="YYYY"
                maxLength={4}
                className="w-full px-3 py-2 rounded-lg border border-gray-200 bg-white text-sm text-gray-800 placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-purple-300 focus:border-purple-400 transition-all"
              />
            </div>
          </div>

          {/* Contextual Fields */}
          <div className="grid md:grid-cols-2 gap-4">
            {srcCfg.fields.includes('journal') && (
              <div>
                <label className="block text-sm font-bold text-gray-700 mb-2 flex items-center gap-2">
                  <i className="ri-article-line text-purple-500"></i>
                  {form.sourceType === 'newspaper' ? 'Newspaper Name' : 'Journal Name'}
                </label>
                <input
                  type="text"
                  value={form.journal ?? ''}
                  onChange={(e) => updateForm({ journal: e.target.value })}
                  placeholder={form.sourceType === 'newspaper' ? 'e.g. The New York Times' : 'e.g. Journal of Educational Technology'}
                  className="w-full px-3 py-2 rounded-lg border border-gray-200 bg-white text-sm text-gray-800 placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-purple-300 focus:border-purple-400 transition-all"
                />
              </div>
            )}

            {srcCfg.fields.includes('publisher') && (
              <div>
                <label className="block text-sm font-bold text-gray-700 mb-2 flex items-center gap-2">
                  <i className="ri-building-line text-purple-500"></i>
                  Publisher
                </label>
                <input
                  type="text"
                  value={form.publisher ?? ''}
                  onChange={(e) => updateForm({ publisher: e.target.value })}
                  placeholder="e.g. Oxford University Press"
                  className="w-full px-3 py-2 rounded-lg border border-gray-200 bg-white text-sm text-gray-800 placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-purple-300 focus:border-purple-400 transition-all"
                />
              </div>
            )}

            {srcCfg.fields.includes('city') && (
              <div>
                <label className="block text-sm font-bold text-gray-700 mb-2 flex items-center gap-2">
                  <i className="ri-map-pin-line text-purple-500"></i>
                  City
                </label>
                <input
                  type="text"
                  value={form.city ?? ''}
                  onChange={(e) => updateForm({ city: e.target.value })}
                  placeholder="e.g. New York"
                  className="w-full px-3 py-2 rounded-lg border border-gray-200 bg-white text-sm text-gray-800 placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-purple-300 focus:border-purple-400 transition-all"
                />
              </div>
            )}

            {srcCfg.fields.includes('url') && (
              <div className="md:col-span-2">
                <label className="block text-sm font-bold text-gray-700 mb-2 flex items-center gap-2">
                  <i className="ri-links-line text-purple-500"></i>
                  URL
                </label>
                <input
                  type="text"
                  value={form.url ?? ''}
                  onChange={(e) => updateForm({ url: e.target.value })}
                  placeholder="https://example.com/article"
                  className="w-full px-3 py-2 rounded-lg border border-gray-200 bg-white text-sm text-gray-800 placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-purple-300 focus:border-purple-400 transition-all"
                />
              </div>
            )}

            {srcCfg.fields.includes('accessDate') && (
              <div>
                <label className="block text-sm font-bold text-gray-700 mb-2 flex items-center gap-2">
                  <i className="ri-calendar-check-line text-purple-500"></i>
                  Access Date
                </label>
                <input
                  type="text"
                  value={form.accessDate ?? ''}
                  onChange={(e) => updateForm({ accessDate: e.target.value })}
                  placeholder="e.g. January 15, 2025"
                  className="w-full px-3 py-2 rounded-lg border border-gray-200 bg-white text-sm text-gray-800 placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-purple-300 focus:border-purple-400 transition-all"
                />
              </div>
            )}

            {srcCfg.fields.includes('conference') && (
              <div className="md:col-span-2">
                <label className="block text-sm font-bold text-gray-700 mb-2 flex items-center gap-2">
                  <i className="ri-presentation-line text-purple-500"></i>
                  Conference Name
                </label>
                <input
                  type="text"
                  value={form.conference ?? ''}
                  onChange={(e) => updateForm({ conference: e.target.value })}
                  placeholder="e.g. International Conference on AI"
                  className="w-full px-3 py-2 rounded-lg border border-gray-200 bg-white text-sm text-gray-800 placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-purple-300 focus:border-purple-400 transition-all"
                />
              </div>
            )}

            {srcCfg.fields.includes('institution') && (
              <div className="md:col-span-2">
                <label className="block text-sm font-bold text-gray-700 mb-2 flex items-center gap-2">
                  <i className="ri-graduation-cap-line text-purple-500"></i>
                  Institution
                </label>
                <input
                  type="text"
                  value={form.institution ?? ''}
                  onChange={(e) => updateForm({ institution: e.target.value })}
                  placeholder="e.g. Stanford University"
                  className="w-full px-3 py-2 rounded-lg border border-gray-200 bg-white text-sm text-gray-800 placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-purple-300 focus:border-purple-400 transition-all"
                />
              </div>
            )}

            {srcCfg.fields.includes('volume') && (
              <div>
                <label className="block text-sm font-bold text-gray-700 mb-2 flex items-center gap-2">
                  <i className="ri-book-line text-purple-500"></i>
                  Volume
                </label>
                <input
                  type="text"
                  value={form.volume ?? ''}
                  onChange={(e) => updateForm({ volume: e.target.value })}
                  placeholder="e.g. 15"
                  className="w-full px-3 py-2 rounded-lg border border-gray-200 bg-white text-sm text-gray-800 placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-purple-300 focus:border-purple-400 transition-all"
                />
              </div>
            )}

            {srcCfg.fields.includes('issue') && (
              <div>
                <label className="block text-sm font-bold text-gray-700 mb-2 flex items-center gap-2">
                  <i className="ri-hashtag text-purple-500"></i>
                  Issue
                </label>
                <input
                  type="text"
                  value={form.issue ?? ''}
                  onChange={(e) => updateForm({ issue: e.target.value })}
                  placeholder="e.g. 3"
                  className="w-full px-3 py-2 rounded-lg border border-gray-200 bg-white text-sm text-gray-800 placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-purple-300 focus:border-purple-400 transition-all"
                />
              </div>
            )}

            {srcCfg.fields.includes('pages') && (
              <div>
                <label className="block text-sm font-bold text-gray-700 mb-2 flex items-center gap-2">
                  <i className="ri-pages-line text-purple-500"></i>
                  Pages
                </label>
                <input
                  type="text"
                  value={form.pages ?? ''}
                  onChange={(e) => updateForm({ pages: e.target.value })}
                  placeholder="e.g. 45-67"
                  className="w-full px-3 py-2 rounded-lg border border-gray-200 bg-white text-sm text-gray-800 placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-purple-300 focus:border-purple-400 transition-all"
                />
              </div>
            )}

            {srcCfg.fields.includes('doi') && (
              <div>
                <label className="block text-sm font-bold text-gray-700 mb-2 flex items-center gap-2">
                  <i className="ri-fingerprint-line text-purple-500"></i>
                  DOI
                </label>
                <input
                  type="text"
                  value={form.doi ?? ''}
                  onChange={(e) => updateForm({ doi: e.target.value })}
                  placeholder="e.g. 10.1234/jet.2022.15.3.45"
                  className="w-full px-3 py-2 rounded-lg border border-gray-200 bg-white text-sm text-gray-800 placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-purple-300 focus:border-purple-400 transition-all"
                />
              </div>
            )}
          </div>

          {/* Build Button */}
          <button
            onClick={handleBuild}
            disabled={!isFormValid || isBuilding}
            className="w-full flex items-center justify-center gap-2 py-3.5 bg-gradient-to-r from-purple-600 to-indigo-600 text-white text-sm font-bold rounded-xl hover:shadow-lg transition-all whitespace-nowrap cursor-pointer disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {isBuilding ? (
              <><i className="ri-loader-4-line animate-spin text-base"></i> Building Citations…</>
            ) : (
              <><i className="ri-magic-line text-base"></i> Build Citation</>
            )}
          </button>

          {!isFormValid && (
            <p className="text-center text-xs text-gray-400">
              Fill in at least: one author's last name, title, and year
            </p>
          )}
        </>
      ) : (
        /* Result View */
        <div className="space-y-4">
          <div className="flex items-center justify-between p-4 rounded-2xl bg-gradient-to-r from-emerald-50 to-teal-50 border border-emerald-200">
            <div className="flex items-center gap-3">
              <div className="w-9 h-9 flex items-center justify-center rounded-xl bg-emerald-100 flex-shrink-0">
                <i className="ri-check-line text-emerald-600 text-lg"></i>
              </div>
              <div>
                <p className="text-sm font-bold text-emerald-800">Citation Built Successfully!</p>
                <p className="text-xs text-emerald-600">Formatted in all 4 styles — copy or save to your list</p>
              </div>
            </div>
            <button
              onClick={handleReset}
              className="flex items-center gap-1.5 px-4 py-2 rounded-lg bg-white border border-emerald-200 text-emerald-700 text-xs font-semibold hover:bg-emerald-50 transition-all cursor-pointer whitespace-nowrap"
            >
              <i className="ri-add-line text-sm"></i>
              Build Another
            </button>
          </div>

          {currentCitation && (
            <div className="space-y-3">
              {(['APA', 'MLA', 'Chicago', 'IEEE'] as CitationStyle[]).map(style => {
                const cfg = STYLE_CONFIG[style];
                const cit = currentCitation.citations[style];
                return (
                  <div key={style} className={`rounded-2xl border-2 ${cfg.border} ${cfg.bg} p-4`}>
                    <div className="flex items-center justify-between mb-2">
                      <span className={`text-xs font-extrabold px-2.5 py-1 rounded-full border ${cfg.bg} ${cfg.color} ${cfg.border}`}>
                        {style}
                      </span>
                      <button
                        onClick={() => handleCopy(cit.full, style)}
                        className={`flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-bold transition-all whitespace-nowrap cursor-pointer ${copiedStyle === style ? 'bg-emerald-500 text-white' : 'bg-white text-gray-600 hover:bg-gray-100'}`}
                      >
                        <i className={`${copiedStyle === style ? 'ri-check-line' : 'ri-file-copy-line'} text-xs`}></i>
                        {copiedStyle === style ? 'Copied!' : 'Copy'}
                      </button>
                    </div>
                    <div className="bg-white rounded-xl px-4 py-3 mb-2 border border-gray-200">
                      <p className="text-xs text-gray-700 leading-relaxed font-mono"
                        dangerouslySetInnerHTML={{ __html: cit.full.replace(/\*(.*?)\*/g, '<em>$1</em>') }}
                      />
                    </div>
                    <div className="flex items-center gap-2">
                      <span className="text-xs text-gray-400 font-medium flex-shrink-0">In-text:</span>
                      <code className={`text-xs font-mono px-2 py-0.5 rounded-lg ${cfg.bg} ${cfg.color} border ${cfg.border}`}>
                        {cit.inText}
                      </code>
                      <button
                        onClick={() => handleCopy(cit.inText, style)}
                        className="text-xs text-gray-400 hover:text-gray-600 transition-colors cursor-pointer whitespace-nowrap"
                      >
                        copy
                      </button>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      )}

      {/* Saved Citations List */}
      {builtCitations.length > 0 && !showResult && (
        <div className="space-y-3">
          <h4 className="text-sm font-extrabold text-gray-800 flex items-center gap-2">
            <i className="ri-bookmark-line text-purple-500"></i>
            Saved Citations
            <span className="px-2 py-0.5 rounded-full bg-purple-100 text-purple-700 text-xs font-bold">{builtCitations.length}</span>
          </h4>
          <div className="space-y-2 max-h-80 overflow-y-auto pr-1">
            {builtCitations.map(cit => {
              const cfg = SOURCE_CONFIG[cit.form.sourceType];
              return (
                <div
                  key={cit.id}
                  className={`rounded-2xl border-2 overflow-hidden transition-all cursor-pointer ${expandedId === cit.id ? 'border-purple-200 shadow-md' : 'border-gray-100 bg-white hover:border-gray-200 hover:shadow-sm'}`}
                  onClick={() => setExpandedId(expandedId === cit.id ? null : cit.id)}
                >
                  <div className={`flex items-center gap-3 p-3 ${expandedId === cit.id ? 'bg-gradient-to-r from-purple-50 to-indigo-50' : 'bg-white'}`}>
                    <div className={`w-7 h-7 flex items-center justify-center rounded-lg flex-shrink-0 ${cfg.bg}`}>
                      <i className={`${cfg.icon} ${cfg.color} text-sm`}></i>
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-semibold text-gray-800 truncate">{cit.form.title}</p>
                      <p className="text-xs text-gray-400 truncate">
                        {cit.form.authors.map(a => `${a.firstName} ${a.lastName}`).join(', ')} · {cit.form.year}
                      </p>
                    </div>
                    <i className={`ri-arrow-down-s-line text-gray-400 text-lg transition-transform flex-shrink-0 ${expandedId === cit.id ? 'rotate-180' : ''}`}></i>
                  </div>
                  {expandedId === cit.id && (
                    <div className="border-t border-gray-100 p-3 space-y-2">
                      {(['APA', 'MLA', 'Chicago', 'IEEE'] as CitationStyle[]).map(style => {
                        const styleCfg = STYLE_CONFIG[style];
                        const citation = cit.citations[style];
                        return (
                          <div key={style} className="flex items-center justify-between gap-2">
                            <span className={`text-xs font-bold px-2 py-0.5 rounded-full ${styleCfg.bg} ${styleCfg.color}`}>{style}</span>
                            <button
                              onClick={(e) => { e.stopPropagation(); handleCopy(citation.full, style); }}
                              className="text-xs text-gray-500 hover:text-gray-700 font-medium cursor-pointer whitespace-nowrap"
                            >
                              {copiedStyle === style ? '✓ Copied' : 'Copy'}
                            </button>
                          </div>
                        );
                      })}
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        </div>
      )}

      {/* Info */}
      {!showResult && (
        <div className="flex items-start gap-3 p-4 rounded-2xl bg-gradient-to-r from-purple-50 to-indigo-50 border border-purple-200">
          <div className="w-8 h-8 flex items-center justify-center rounded-lg bg-purple-100 flex-shrink-0">
            <i className="ri-lightbulb-line text-purple-600 text-base"></i>
          </div>
          <div>
            <p className="text-sm font-bold text-purple-800 mb-1">How Reference Builder Works</p>
            <p className="text-xs text-purple-700 leading-relaxed">
              Select your source type, fill in the required fields (author, title, year), and click <strong>Build Citation</strong>. The tool instantly generates properly formatted citations in APA, MLA, Chicago, and IEEE styles. All built citations are saved to your list for easy access.
            </p>
          </div>
        </div>
      )}
    </div>
  );
}